/**
 * Periodic getStats() snapshots — feeds a minimal HUD overlay (RTT, packet loss, fps, bitrate).
 * Implementation is deliberately small; richer analytics arrive in Sprint 8.
 */

export interface StatsSnapshot {
  /** Wall-clock at snapshot time. */
  takenAt: number;
  /** Inbound video (we are receiver). */
  video?: {
    framesPerSecond?: number;
    framesDecoded?: number;
    keyFramesDecoded?: number;
    framesDropped?: number;
    bytesReceived?: number;
    bitrateKbps?: number;
    width?: number;
    height?: number;
    codec?: string;
    jitter?: number;
    packetsLost?: number;
  };
  /** Selected ICE candidate pair. */
  transport?: {
    currentRttMs?: number;
    localCandidateType?: string;
    remoteCandidateType?: string;
    bytesReceived?: number;
    bytesSent?: number;
  };
}

interface InternalState {
  lastBytes: number;
  lastTakenAt: number;
  lastFramesDecoded: number;
}

/** lib.dom doesn't ship RTCIceCandidateStats in all TS versions; mirror the fields we actually read. */
interface RTCIceCandidateStatsLite {
  candidateType?: string;
}

export function createStatsSampler(pc: RTCPeerConnection) {
  const state: InternalState = { lastBytes: 0, lastTakenAt: 0, lastFramesDecoded: 0 };
  const codecsByPayloadType = new Map<string, string>();

  async function sample(): Promise<StatsSnapshot> {
    const now = Date.now();
    const report = await pc.getStats();
    const out: StatsSnapshot = { takenAt: now };

    // First pass: codec lookup table.
    report.forEach((s) => {
      if (s.type === 'codec') {
        codecsByPayloadType.set(String(s.id), String((s as RTCRtpCodec & { mimeType?: string }).mimeType ?? ''));
      }
    });

    // Find the currently selected candidate pair via the active transport.
    let activeTransport: { selectedCandidatePairId?: string } | undefined;
    report.forEach((s) => {
      if (s.type === 'transport' && (s as RTCTransportStats).dtlsState === 'connected') {
        activeTransport = s as { selectedCandidatePairId?: string };
      }
    });
    if (activeTransport?.selectedCandidatePairId) {
      const pair = report.get(activeTransport.selectedCandidatePairId) as RTCIceCandidatePairStats | undefined;
      if (pair) {
        const local = report.get(pair.localCandidateId) as RTCIceCandidateStatsLite | undefined;
        const remote = report.get(pair.remoteCandidateId) as RTCIceCandidateStatsLite | undefined;
        out.transport = {
          currentRttMs: pair.currentRoundTripTime !== undefined ? pair.currentRoundTripTime * 1000 : undefined,
          localCandidateType: local?.candidateType,
          remoteCandidateType: remote?.candidateType,
          bytesReceived: pair.bytesReceived,
          bytesSent: pair.bytesSent,
        };
      }
    }

    report.forEach((s) => {
      if (s.type === 'inbound-rtp' && (s as RTCInboundRtpStreamStats).kind === 'video') {
        const v = s as RTCInboundRtpStreamStats & {
          framesPerSecond?: number;
          framesDecoded?: number;
          keyFramesDecoded?: number;
          framesDropped?: number;
          frameWidth?: number;
          frameHeight?: number;
          codecId?: string;
        };
        const bytes = v.bytesReceived ?? 0;
        const delta = state.lastTakenAt ? bytes - state.lastBytes : 0;
        const elapsedSec = state.lastTakenAt ? (now - state.lastTakenAt) / 1000 : 0;
        const bitrate = elapsedSec > 0 ? (delta * 8) / 1000 / elapsedSec : 0;
        state.lastBytes = bytes;
        state.lastTakenAt = now;
        state.lastFramesDecoded = v.framesDecoded ?? state.lastFramesDecoded;
        out.video = {
          framesPerSecond: v.framesPerSecond,
          framesDecoded: v.framesDecoded,
          keyFramesDecoded: v.keyFramesDecoded,
          framesDropped: v.framesDropped,
          bytesReceived: v.bytesReceived,
          bitrateKbps: Math.round(bitrate),
          width: v.frameWidth,
          height: v.frameHeight,
          codec: v.codecId ? codecsByPayloadType.get(String(v.codecId)) : undefined,
          jitter: v.jitter,
          packetsLost: v.packetsLost,
        };
      }
    });

    return out;
  }

  return { sample };
}
